import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { effectVibes } from '../data/products';
import { useProducts } from '../context/ProductsContext';

// Only show categories that have products AND a display label
const CATEGORY_LABELS = {
  All: 'All',
  vapes: 'Vapes',
  pods: 'Pods',
  shisha: 'Shisha',
  flower: 'Flower',
  accessories: 'Accessories',
  drinks: 'Drinks',
  gummies: 'Gummies',
  bundles: 'Bundles',
};

// Derive categories dynamically from actual product data so no blank pills ever appear
const buildCategories = (products) => {
  const inData = new Set(products.map(p => p.category));
  return ['All', ...Object.keys(CATEGORY_LABELS).filter(k => k !== 'All' && inData.has(k))];
};

const ShopPage = ({ onAddToCart }) => {
  const { products } = useProducts();
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeEffect, setActiveEffect] = useState('All');
  const [sortBy, setSortBy] = useState('featured');
  const [filtersOpen, setFiltersOpen] = useState(false);

  const categories = useMemo(() => buildCategories(products), [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (activeCategory !== 'All') list = list.filter(p => p.category === activeCategory);
    if (activeEffect !== 'All') list = list.filter(p => p.effect === activeEffect);
    if (sortBy === 'price-asc') list.sort((a, b) => a.price - b.price);
    if (sortBy === 'price-desc') list.sort((a, b) => b.price - a.price);
    if (sortBy === 'rating') list.sort((a, b) => b.rating - a.rating);
    return list;
  }, [products, activeCategory, activeEffect, sortBy]);

  const catLabels = CATEGORY_LABELS;

  return (
    <div style={{ paddingTop: '68px', minHeight: '100vh', background: 'var(--cream)' }}>
      {/* Header */}
      <div style={{
        background: 'var(--forest)',
        padding: 'clamp(36px, 6vw, 60px) clamp(16px, 4vw, 32px)',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', right: '-100px', top: '-100px',
          width: '400px', height: '400px', borderRadius: '50%',
          background: 'rgba(201,168,76,0.1)',
        }} />
        <div style={{ maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
          <p style={{
            fontSize: 'clamp(0.68rem, 2vw, 0.75rem)', fontWeight: 700, letterSpacing: '0.12em',
            textTransform: 'uppercase', color: 'var(--gold)',
            marginBottom: '10px',
          }}>Premium Selection</p>
          <h1 style={{
            fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(2rem, 6vw, 3.2rem)',
            color: 'var(--white)', letterSpacing: '-0.01em', marginBottom: '8px',
          }}>
            The Collection
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 'clamp(0.82rem, 2.5vw, 0.95rem)' }}>
            {products.length} products · All third-party lab tested
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: 'clamp(20px, 4vw, 32px) clamp(16px, 4vw, 32px)' }}>
        {/* Filter bar */}
        <div style={{ marginBottom: '28px' }}>
          {/* Row 1: scrollable category pills */}
          <div style={{
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            marginBottom: '10px',
          }} className="cat-scroll">
            <div style={{
              display: 'flex', gap: '6px',
              width: 'max-content',
              paddingBottom: '4px',
            }}>
              {categories.map(cat => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  style={{
                    padding: '8px 16px', borderRadius: '999px',
                    background: activeCategory === cat ? 'var(--forest)' : 'var(--white)',
                    color: activeCategory === cat ? 'var(--white)' : 'var(--mid)',
                    border: activeCategory === cat ? 'none' : '1.5px solid var(--mist)',
                    fontFamily: 'Syne', fontWeight: 600, fontSize: '0.8rem',
                    cursor: 'pointer', transition: 'all 0.2s', letterSpacing: '0.02em',
                    whiteSpace: 'nowrap', flexShrink: 0,
                  }}
                >
                  {catLabels[cat]}
                </button>
              ))}
            </div>
          </div>

          {/* Row 2: sort + filter button */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              style={{
                flex: 1,
                padding: '8px 14px', borderRadius: '999px',
                border: '1.5px solid var(--mist)',
                background: 'var(--white)',
                fontFamily: 'DM Sans', fontSize: '0.82rem',
                color: 'var(--charcoal)', cursor: 'pointer', outline: 'none',
                maxWidth: '200px',
              }}
            >
              <option value="featured">Featured</option>
              <option value="rating">Top Rated</option>
              <option value="price-asc">Price: Low–High</option>
              <option value="price-desc">Price: High–Low</option>
            </select>

            <button
              onClick={() => setFiltersOpen(!filtersOpen)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '999px',
                background: filtersOpen ? 'var(--forest)' : 'var(--white)',
                color: filtersOpen ? 'var(--white)' : 'var(--mid)',
                border: filtersOpen ? 'none' : '1.5px solid var(--mist)',
                fontFamily: 'Syne', fontWeight: 600, fontSize: '0.8rem',
                cursor: 'pointer', transition: 'all 0.2s',
                whiteSpace: 'nowrap', flexShrink: 0,
              }}
            >
              <SlidersHorizontal size={13} /> Filters
            </button>
          </div>
        </div>

        {/* Effect filter */}
        {filtersOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{
              background: 'var(--white)', borderRadius: '16px',
              padding: 'clamp(16px, 3vw, 24px)', marginBottom: '24px',
              border: '1px solid var(--mist)',
            }}
          >
            <div style={{
              display: 'flex', alignItems: 'center',
              justifyContent: 'space-between', marginBottom: '14px',
            }}>
              <h4 style={{
                fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(0.78rem, 2vw, 0.85rem)',
                letterSpacing: '0.05em', textTransform: 'uppercase',
                color: 'var(--charcoal)',
              }}>Filter by Effect</h4>
              <button
                onClick={() => { setActiveEffect('All'); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: '0.78rem', color: 'var(--mid)',
                  fontFamily: 'DM Sans', fontWeight: 500,
                }}
              >Clear All</button>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {effectVibes.map(effect => (
                <button
                  key={effect}
                  onClick={() => setActiveEffect(effect)}
                  style={{
                    padding: '7px 16px', borderRadius: '999px',
                    background: activeEffect === effect ? 'var(--forest)' : 'var(--smoke)',
                    color: activeEffect === effect ? 'var(--white)' : 'var(--mid)',
                    border: 'none',
                    fontFamily: 'Syne', fontWeight: 600, fontSize: 'clamp(0.72rem, 2vw, 0.8rem)',
                    cursor: 'pointer', transition: 'all 0.2s',
                  }}
                >
                  {effect}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Active filters display */}
        {(activeCategory !== 'All' || activeEffect !== 'All') && (
          <div style={{
            display: 'flex', gap: '8px', marginBottom: '20px',
            flexWrap: 'wrap', alignItems: 'center',
          }}>
            <span style={{ fontSize: '0.8rem', color: 'var(--mid)', fontWeight: 500 }}>Active:</span>
            {activeCategory !== 'All' && (
              <span style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'var(--forest)', color: 'var(--white)',
                borderRadius: '999px', padding: '4px 12px',
                fontSize: '0.78rem', fontWeight: 600,
              }}>
                {catLabels[activeCategory]}
                <button onClick={() => setActiveCategory('All')} style={{
                  background: 'none', border: 'none', color: 'inherit',
                  cursor: 'pointer', padding: 0, display: 'flex',
                }}><X size={12} /></button>
              </span>
            )}
            {activeEffect !== 'All' && (
              <span style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                background: 'var(--gold)', color: 'var(--white)',
                borderRadius: '999px', padding: '4px 12px',
                fontSize: '0.78rem', fontWeight: 600,
              }}>
                {activeEffect}
                <button onClick={() => setActiveEffect('All')} style={{
                  background: 'none', border: 'none', color: 'inherit',
                  cursor: 'pointer', padding: 0, display: 'flex',
                }}><X size={12} /></button>
              </span>
            )}
          </div>
        )}

        {/* Products count */}
        <p style={{
          fontSize: 'clamp(0.78rem, 2vw, 0.85rem)', color: 'var(--mid)',
          marginBottom: '20px', fontWeight: 500,
        }}>Showing {filtered.length} product{filtered.length !== 1 ? 's' : ''}</p>

        {/* Products grid — 2 cols on mobile, auto-fill on larger screens */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 'clamp(10px, 2vw, 22px)',
        }} className="products-grid">
          {filtered.map((product, i) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
            >
              <ProductCard product={product} onAddToCart={onAddToCart} />
            </motion.div>
          ))}
        </div>

        {filtered.length === 0 && (
          <div style={{
            textAlign: 'center', padding: '80px 20px',
          }}>
            <div style={{ marginBottom: '16px' }}>
              <SlidersHorizontal size={48} color="var(--mist)" style={{ margin: '0 auto' }} />
            </div>
            <h3 style={{
              fontFamily: 'Syne', fontWeight: 700, fontSize: 'clamp(1.1rem, 3vw, 1.3rem)',
              color: 'var(--charcoal)', marginBottom: '8px',
            }}>No products found</h3>
            <p style={{ color: 'var(--mid)', fontSize: '0.9rem' }}>
              Try adjusting your filters
            </p>
          </div>
        )}
      </div>

      <style>{`
        @media (min-width: 640px) {
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)) !important;
          }
        }
      `}</style>
    </div>
  );
};

export default ShopPage;